import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';

import {
  fetchMyLanguage,
  languageKeys,
  updateMyLanguage,
  type MyLanguage,
} from '@/features/i18n/api/languages';
import { I18nProvider } from '@/lib/i18n/I18nProvider';
import { DEFAULT_LANGUAGE, languageMeta } from '@/lib/i18n/languages';
import { availableDictionaryLanguages } from '@/lib/i18n/loader';

/**
 * The language of the screens a merchant meets before the dashboard: sign in,
 * sign up, the email code, password resets, an invitation, onboarding and the
 * plan picker.
 *
 * The dashboard keeps its language on the server, per person - and nobody has
 * a person yet on the sign-in page. So the choice made here lives in this
 * browser, and once somebody is signed in (onboarding, the plan picker) it is
 * also saved as their dashboard language, so the dashboard opens in the
 * language they were already reading. A signed-in person who chose one inside
 * the dashboard earlier gets that one here too.
 */
export const PRE_DASHBOARD_LANGUAGE_KEY = 'neon.preDashboardLanguage';

const TRANSLATED = new Set(availableDictionaryLanguages());

function renderable(code: string | null | undefined): string | null {
  if (!code) return null;
  const normalised = languageMeta(code).code;
  return TRANSLATED.has(normalised) ? normalised : null;
}

export function readPreDashboardLanguage(): string | null {
  try {
    return renderable(window.localStorage.getItem(PRE_DASHBOARD_LANGUAGE_KEY));
  } catch {
    return null;
  }
}

function savePreDashboardLanguage(code: string) {
  try {
    window.localStorage.setItem(PRE_DASHBOARD_LANGUAGE_KEY, code);
  } catch {
    // Private mode or blocked storage: the choice lasts as long as the page.
  }
}

export function PreDashboardI18n({
  children,
  signedIn = false,
}: {
  children: ReactNode;
  /** The person has an account session, so their saved language can be read and written. */
  signedIn?: boolean;
}) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(readPreDashboardLanguage);
  const mine = useQuery({
    queryKey: languageKeys.mine,
    queryFn: fetchMyLanguage,
    enabled: signedIn,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const language =
    (signedIn ? renderable(mine.data?.effective) : null) ?? saved ?? DEFAULT_LANGUAGE;

  const onLanguageChange = useCallback(
    (code: string) => {
      savePreDashboardLanguage(code);
      setSaved(code);
      if (!signedIn) return;
      const previous = queryClient.getQueryData<MyLanguage>(languageKeys.mine);
      queryClient.setQueryData<MyLanguage>(languageKeys.mine, {
        preferred: code,
        effective: code,
        store_admin_language: previous?.store_admin_language ?? null,
      });
      // Best effort: the screen has already switched, and the browser keeps
      // the choice even if the account could not.
      updateMyLanguage(code)
        .then((data) => queryClient.setQueryData(languageKeys.mine, data))
        .catch(() => undefined);
    },
    [queryClient, signedIn],
  );

  return (
    <I18nProvider scope="admin" applyToDocument language={language} onLanguageChange={onLanguageChange}>
      {children}
    </I18nProvider>
  );
}
